/**
 * The picture of an agent, as the params schema wants it: a data URI of a small square image.
 *
 * The chosen file never leaves the browser as it is: it is cropped to a centred square of
 * AVATAR_SIZE pixels and encoded as WebP (JPEG where the browser cannot encode WebP), lowering the
 * quality until it fits in the length the schema allows.
 */

export const AVATAR_SIZE = 256;

const QUALITIES = [0.9, 0.8, 0.65, 0.5];

/**
 * The largest file read, in bytes: decoding a picture takes width x height x 4 bytes of memory,
 * and a small file can hold a huge picture. A photo from a phone is well below it.
 */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/** The longest side of a picture that is decoded, in pixels. */
export const MAX_SIDE = 10000;

/** The square crop of an image of `width` x `height`: centred, as large as the shorter side. */
export function squareCrop(width, height) {
  const side = Math.min(width, height);
  return { x: Math.round((width - side) / 2), y: Math.round((height - side) / 2), side };
}

/** The first letter of a name, shown in place of a missing picture. */
export function initialOf(name) {
  return (name ?? '').trim().charAt(0).toUpperCase() || '?';
}

/** The picture in `file` as a data URI that fits in `maxLength`; rejects with a message for the user. */
export async function avatarFromFile(file, maxLength) {
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image: a PNG, JPEG, WebP or GIF file.');
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('This image is too large: choose one of at most 20 MB.');
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('This image cannot be read: try a PNG or JPEG file.');
  }
  if (bitmap.width > MAX_SIDE || bitmap.height > MAX_SIDE) {
    bitmap.close();
    throw new Error(`This image is too large: choose one of at most ${MAX_SIDE} pixels per side.`);
  }
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const crop = squareCrop(bitmap.width, bitmap.height);
  const context = canvas.getContext('2d');
  // JPEG has no transparency: a transparent logo gets a white background instead of a black one
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  context.drawImage(bitmap, crop.x, crop.y, crop.side, crop.side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
  bitmap.close();
  for (const quality of QUALITIES) {
    let uri = canvas.toDataURL('image/webp', quality);
    if (!uri.startsWith('data:image/webp;')) {
      uri = canvas.toDataURL('image/jpeg', quality);
    }
    if (uri.length <= maxLength) {
      return uri;
    }
  }
  throw new Error('This image is too detailed to be stored: try a simpler one.');
}
